"use client";
import { Box, Button, Grid, Link, Typography } from "@mui/material";
import FormTextFieldSimple from "../formulari/FormTextFieldSimple";
import { pxToVw } from "@/utils/pxToVw";
import { getTextWithContext, useText, useTexts } from "@/context/TextContext";
import { loginSchema } from "@/lib/schemas/login";
import { useActionState, useEffect, useState } from "react";
import z from "zod";
import FormWrapper from "../FormWrapper";
import { loginAction } from "@/app/actions";
import { useRouter } from "next/navigation";
import { MOBILE_BASE } from "@/theme/theme";

export default function LoginForm() {
  const [form, setForm] = useState({ email: "", pass: "" });
  const [errors, setErrors] = useState({} as any);
  const [state, formAction, pending] = useActionState(loginAction, {} as any);

  const router = useRouter();
  const context = useTexts();

  useEffect(() => {
    if (state?.errors) {
      setErrors(state.errors);
    }
  }, [state, router, context.locale]);

  const validateField = (fieldName: keyof typeof form) => {
    const data = {
      [fieldName]: form[fieldName] || undefined,
    };
    const result = loginSchema.partial().safeParse(data);
    setErrors((prev: any) => ({
      ...prev,
      [fieldName]: result.success
        ? undefined
        : (z.treeifyError(result.error).properties as any)?.[fieldName],
    }));
  };

  const handleChange = (value: any, fieldName: keyof typeof form) => {
    setForm((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
  };

  return (
    <Grid
      size={{ xs: 12, md: 6 }}
      bgcolor={{ xs: "#F6F5F5", md: "transparent" }}
      px={{ xs: pxToVw(16, MOBILE_BASE), md: 0 }}
      py={{ xs: pxToVw(22, MOBILE_BASE), md: 0 }}
    >
      <Typography
        variant="body3"
        color="marron"
        mb={{ xs: pxToVw(18, MOBILE_BASE), md: pxToVw(18) }}
        component={"p"}
        fontSize={{
          xs: pxToVw(25, MOBILE_BASE),
          md: pxToVw(24, undefined, 32),
        }}
      >
        {useText("login.form.title")}
      </Typography>
      <Typography
        variant="body5"
        color="marron"
        mb={{ xs: pxToVw(47, MOBILE_BASE), md: pxToVw(47) }}
      >
        {useText("login.form.subtitle")}
      </Typography>
      <FormWrapper action={formAction} errors={errors} pending={pending}>
        <Box mb={{ xs: pxToVw(35, MOBILE_BASE), md: pxToVw(45) }}>
          <FormTextFieldSimple
            name="email"
            startAdornmentText={useText("login.form.emailLabel")}
            autoComplete="email"
            value={form.email}
            error={!!errors.email}
            helperText={
              errors.email
                ? errors.email.errors[1]
                  ? getTextWithContext(errors.email.errors[1], context)
                  : getTextWithContext(errors.email.errors[0], context)
                : ""
            }
            onBlur={(e) => validateField("email")}
            onChange={(e) => {
              handleChange(e.target.value, "email");
            }}
          />
        </Box>
        <FormTextFieldSimple
          name="pass"
          type="password"
          startAdornmentText={useText("login.form.passwordLabel")}
          autoComplete="current-password"
          value={form.pass}
          error={!!errors.pass}
          helperText={
            errors.pass
              ? getTextWithContext(errors.pass.errors[0], context)
              : ""
          }
          onBlur={(e) => validateField("pass")}
          onChange={(e) => {
            handleChange(e.target.value, "pass");
          }}
        />
        <Link
          variant="body5"
          color="marron"
          my={pxToVw(39)}
          mt={{ xs: pxToVw(49, MOBILE_BASE), md: pxToVw(39) }}
          mb={{ xs: pxToVw(25, MOBILE_BASE), md: pxToVw(39) }}
          sx={{ textDecoration: "underline", display: "flex" }}
          href={useText("login.form.forgotPasswordLink")}
        >
          {useText("login.form.forgotPassword")}
        </Link>
        <Grid container>
          <Button
            variant="botonMarronInverso"
            id="button-login-entrar"
            type="submit"
          >
            {useText("login.form.loginButton")}
          </Button>
        </Grid>
      </FormWrapper>
    </Grid>
  );
}
